import './App.css';
import { toast, Toaster, useToasterStore } from 'react-hot-toast';
import { useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import { useTranslation } from 'react-i18next';
import { ApolloProvider } from '@apollo/client';
import RootRouter from './components/RootRoute/RootRoute';
import ThemeWrapper from './components/ThemeWrapper/ThemeWrapper';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { fetchCoachRequest, setCoachIsLoggedIn } from './redux/coach/coachSlice';
import progressMagicJwtToken, { authTokenSilentRefreshCycleTime } from './config/constants';
import { isTokenAboutToExpire, isTokenExists, isTokenValid } from './utils/auth';
import { refreshAccessToken } from './service/api/authApi';
import TawkMessenger from './components/TwakTo/TwakMessenger';
import client from './service/graphql';

const { fetch: originalFetch } = window;

// TODO: have to include token validation logic here

const App = () => {
  const dispatch = useAppDispatch();
  const { toasts } = useToasterStore();
  const isLoggedIn = useAppSelector((state) => state.coach.loggedInStatus);
  const { coach } = useAppSelector((state) => state.coach);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  }, []);

  axios.interceptors.request.use(async (config) => {
    const appLanguage = localStorage.getItem('i18nextLng') || 'en';
    config.headers.set('x-custom-lang', appLanguage);
    return config;
  });
  /* eslint-disable-next-line */
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const resource = input instanceof URL ? input.href : input;

    const options = init || {};

    if (!resource.toString().includes('tawk')) {
      const appLanguage = localStorage.getItem('i18nextLng') || 'en';

      if (options.headers) {
        if (!(options.headers instanceof Headers)) {
          options.headers = new Headers(options.headers);
        }
      } else {
        options.headers = new Headers();
      }

      options.headers.set('x-custom-lang', appLanguage);

      const token = localStorage.getItem(progressMagicJwtToken);
      if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return originalFetch(resource, options);
  };

  const runSilentTokenRefresh = async () => {
    if (
      isTokenExists() &&
      isTokenAboutToExpire(localStorage.getItem(progressMagicJwtToken) as string)
    ) {
      // Refreshing the token in silent
      await refreshAccessToken();
    }
  };

  const handleAppInit = async () => {
    if (isTokenValid()) {
      dispatch(fetchCoachRequest());
    }
    if (isTokenExists()) {
      // Run silent token validation on init
      runSilentTokenRefresh();
      // Run silent token validation every 30 minutes
      setInterval(() => {
        runSilentTokenRefresh();
      }, authTokenSilentRefreshCycleTime);
    } else {
      dispatch(setCoachIsLoggedIn(false));
    }
  };

  useEffect(() => {
    handleAppInit();
  }, []);

  useEffect(() => {
    const uniqueToasts = new Set();
    toasts
      .filter((t) => t.visible)
      .forEach((t) => {
        const message = t.message || t.id;
        if (uniqueToasts.has(message)) {
          toast.dismiss(t.id);
        } else {
          uniqueToasts.add(message);
        }
      });
  }, [toasts]);

  // Language change handling for dayjs
  const currentLanguage = i18n.language;
  dayjs.locale(currentLanguage);

  return (
    <ApolloProvider client={client}>
      <ThemeWrapper>
        <RootRouter />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#fff',
              color: '#001E1D',
            },
            duration: 5000,
            iconTheme: {
              primary: '#004643',
              secondary: '#fff',
            },
          }}
        />
        {isLoggedIn && coach && (
          <TawkMessenger name={coach?.firstName as string} email={coach?.email as string} />
        )}
      </ThemeWrapper>
    </ApolloProvider>
  );
};

export default App;
